/**
 * Iter60.p48.1 — Module page permission gate.
 *
 * Wraps a page component and renders a friendly permission-denied
 * banner (with a "Back to Home" affordance) when the current user's
 * ROLE_DEFAULTS omit `perm`. Nav items already hide the corresponding
 * URLs for blocked roles — this boundary is a safety net for shared
 * links / bookmarks / typed URLs.
 *
 * Passes children through unchanged when `perm` is falsy or the user
 * has the permission. Uses the same `useHasPermission` hook the nav
 * rows use so behaviour stays consistent.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useHasPermission } from "../contexts/PermissionsContext";

export default function RequirePagePermission({ perm, moduleId = null, children }) {
    // Hook must always run before any early-return.
    const allowed = useHasPermission(perm || "", moduleId);
    if (!perm || allowed) return children;
    return (
        <div className="min-h-[60vh] flex items-center justify-center p-10" data-testid="page-permission-denied">
            <div className="asl-card p-8 max-w-md text-center space-y-4">
                <div className="text-6xl font-bold asl-crimson-text">403</div>
                <h2 className="text-lg font-semibold asl-navy-text" data-testid="page-permission-denied-title">
                    You don't have permission to view this page.
                </h2>
                <p className="text-sm text-[color:var(--asl-muted)]">
                    Contact Super Admin or HR Manager if you believe this is a mistake.
                </p>
                <Link
                    to="/"
                    className="inline-block px-4 py-2 rounded asl-navy-bg text-white text-sm font-medium hover:opacity-90"
                    data-testid="page-permission-denied-back"
                >
                    ← Back to Home
                </Link>
            </div>
        </div>
    );
}
